import {Component, effect, Inject, inject, OnDestroy, Signal, TrackByFunction} from '@angular/core';
import {last, map, switchMap, take} from 'rxjs/operators';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Apollo} from 'apollo-angular';
import {Airline, CodecType, Mutation, StartWorkflowDefaultInput, TemplateCapabilities, TrackRequirement} from '../../../types-workflow';

import {START_WORKFLOW_DEFAULT, TEMPLATE_CAPABILITIES} from '../../../queries-workflow';
import {COPY_FILE} from '../../../queries-repository';
import {
  TITLECARD_BURNIN_POSITION,
  TITLECARD_LANG,
  titlecardDataUri,
  titlecardDestination,
  titlecardPrefill,
} from '../titlecard/titlecard';
import liltDomainsBundle from '../../../../assets/lilt-domains.json';
import {LiltDomain, LiltDomainsFile} from '../../../lilt-domains.types';

// Lilt currently requires exactly one subtitle or closed-caption track per item.
// Audio-only translation (Lilt would use the video's audio as the source) is on Lilt's
// roadmap; when it lands, flip this flag to true and no other change is needed — the
// backend manifest writer and upload Lambda already support the 0-text-track path.
const LILT_AUDIO_ONLY_SUPPORTED = false;

// Seed text for the job-instructions textarea when the "Source review required" checkbox
// is toggled. Operator can edit freely afterwards; toggling the checkbox re-seeds.
const LILT_INSTRUCTIONS_REVIEW_REQUIRED =
  'Source review required. Please perform source review before starting translation.';
const LILT_INSTRUCTIONS_REVIEW_NOT_REQUIRED =
  'Source review not required. The source material does not need to be reviewed.';
import {forkJoin, Observable, of, Subscription} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {ALL_SYSTEMS, DELTA_SYSTEMS, IfeFormat, PoDestination, THALES_SYSTEMS} from '../../../target_systems';
import {BasicPoitemInfoComponent} from '../basics-poitem-info/basic-poitem-info.component';
import {TrackSelectionComponent} from '../tracks-selection/track-selection.component';
import {clean_datetime} from '../../utils';
import {poNumberValidator} from '../../name-limits';
import {Mutation as RepositoryMutation, Organization} from '../../../types-repository';
import {OAuthService} from 'angular-oauth2-oidc';
import {Po2024Service} from '../../../services/po-2024.service';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {ProcessService} from '../../../services/process.service';
import {DefaultSpecificComponent} from '../default-specific/default-specific.component';
import {DefaultFormatSpecificComponent} from '../default-format-specific/default-format-specific.component';
import {SchemaFormatSpecificComponent} from '../schema-form/schema-format-specific.component';
import {JsonSchema, parseSchema} from '../schema-form/fsd-schema';
import {SourceDurations} from '../source-duration';
import {WatermarkSelection} from '../default-specific/default-specific.component';

import {reportInvalid, startEncodeLabel} from '../form-messages';
import {FormFooterComponent} from '../../form-footer/form-footer.component';
import {ContextService} from '../../../services/context.service';

/** The `po_name` this form submits — the single place it is composed. */
function composePoName(form: AbstractControl): string {
  return `${form.get('airline')?.value ?? ''}_${(form.get('po_number')?.value ?? '').trim()}`;
}

/**
 * The LILT due date picked as a calendar day (YYYY-MM-DD) → the end of that day in the operator's
 * local time (23:59:59), as an ISO 8601 UTC instant. null when none was picked.
 */
export function liltDueInstant(day: string | null | undefined): string | null {
  if (!day) return null;
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59).toISOString();
}

/** An item key that belongs to the destination, not to the template (see the submit). */
function isDestinationKey(key: string): boolean {
  return key === 'thales_fastpath_airline' || key.startsWith('lilt_');
}

/**
 * An item saved before destination_specific existed keeps the destination's keys
 * (thales_fastpath_airline, lilt_*) in format_specific_data, where the schema form would drop
 * them. They are moved over, so a restored PO keeps what the operator entered.
 */
function movedDestinationKeys(poItem: any): any {
  if (!poItem || poItem.destination_specific || !poItem.format_specific_data) {
    return poItem;
  }
  const fsd: any = {};
  const destination: any = {};
  for (const [k, v] of Object.entries(poItem.format_specific_data)) {
    (isDestinationKey(k) ? destination : fsd)[k] = v;
  }
  return {...poItem, format_specific_data: fsd, destination_specific: destination};
}

@Component({
  selector: 'app-default-po',
  templateUrl: './default-po.component.html',
  styleUrls: ['./default-po.component.css'],
  imports: [
    FormFooterComponent,
    ReactiveFormsModule,
    BasicPoitemInfoComponent,
    TrackSelectionComponent,
    DefaultSpecificComponent,
    DefaultFormatSpecificComponent,
    SchemaFormatSpecificComponent,]
})
export class DefaultPoComponent implements OnDestroy {

  organization: Signal<Organization>;
  airlines: Signal<Airline[]>;

  availableSystems = []

  ifeFormats: IfeFormat[] = []
  poDestinations: PoDestination[] = []
  poFormGroup: FormGroup;
  ifeFormat: IfeFormat;
  // Whether the selected format's template can burn in a text track (template_capabilities, from vtkpy).
  // null while unknown or loading: the burnt-in options are hidden until the API has answered.
  burnInSupported: boolean | null = null;
  // The selected format's template_capabilities; null while unknown or loading. The item form,
  // the tracks sent and the format_specific_data schema all come from here, never from the
  // template's name.
  capabilities: TemplateCapabilities | null = null;
  // The template's format_specific_data JSON Schema (parsed AWSJSON); null while unknown.
  fsdSchema: JsonSchema | null = null;
  // How to preselect the job-level watermark switch; null when the template cannot watermark.
  watermarkSelection: WatermarkSelection | null = null;
  // Source video duration (seconds) per item source (`input_brefix`).
  private readonly sourceDurations = new SourceDurations(inject(Apollo));
  /** What the selected format's template delivers (template_capabilities.description); null while unknown. */
  templateDescription: string | null = null;
  private capabilitiesSub?: Subscription;
  readonly titlecardPrefill = titlecardPrefill;

  // Lilt domains are bundled at build time (see scripts/fetch_lilt_domains.py +
  // the CDK deploy-platform-ui workflow). The chosen domain is passed down to each
  // item's format-specific component so it can populate its target-language picker.
  readonly liltDomains: LiltDomain[] = (liltDomainsBundle as LiltDomainsFile).domains;

  subscriptions: Subscription[] = [];

  constructor(
    private apollo: Apollo,
    @Inject('above_default') protected poService: Po2024Service,
    private formBuilder: FormBuilder,
    oAuthService: OAuthService,
    private processService: ProcessService,
    private activatedRoute: ActivatedRoute,
    contextService: ContextService,
  ) {
    this.organization = contextService.organization;
    this.airlines = contextService.airlines

    if (this.activatedRoute.snapshot.params['organization_id'] === 'thales') {
      this.availableSystems = [].concat(DELTA_SYSTEMS, THALES_SYSTEMS);
    } else {
      this.availableSystems = ALL_SYSTEMS;
    }


    this.poFormGroup = formBuilder.group({
      po_number: [clean_datetime(new Date()), [Validators.required, poNumberValidator(composePoName)]],
      airline: [null, Validators.required],
      // Required: start_workflow_default takes po_destination as String!, so a PO without one
      // was refused by the API at submit; every format offers at least one destination.
      po_destination: [null, Validators.required],
      auto_publish: [false],
      due_date: ['now'],
      poItems: formBuilder.array([], Validators.required),
      date_created: [null],
      email_notification: [null, [Validators.required, Validators.email]],
      email_notification_publish: [null, [Validators.email]],
      ife: [null, Validators.required],
      ife_format: [null, Validators.required],

    });

    // The PO number's limits are on the composed po_name, which also carries the airline.
    this.poFormGroup.controls['airline'].valueChanges.subscribe(() => {
      this.poFormGroup.controls['po_number'].updateValueAndValidity();
    })
    this.poFormGroup.controls['ife'].valueChanges.subscribe(value => {
      this.ifeFormats = this.availableSystems.find(as => as.item_id === value)?.formats || [];
      console.log('Ife changed', value)
      if (!this.poFormGroup.controls['ife_format'].value ||
        (
          this.poFormGroup.controls['ife_format'].value &&
          !this.ifeFormats.find(f => f.item_id === this.poFormGroup.controls['ife_format'].value))) {
        console.log('Default to first ife format')
        this.poFormGroup.controls['ife_format'].setValue(this.ifeFormats[0]?.item_id);
      } else {
        console.log('Keeping ife format')
      }
    })
    this.poFormGroup.controls['ife_format'].valueChanges.subscribe(value => {
      this.ifeFormat = this.ifeFormats.find(f => f.item_id === value)
      console.log('Ife format changed', this.ifeFormat)
      this.loadCapabilities(this.ifeFormat?.vtk_template);
      this.poDestinations = this.ifeFormat?.po_destinations || [];
      console.log('poDestinations', this.poDestinations)

      if (this.ifeFormat?.po_pattern) {
        this.poFormGroup.controls['po_number'].setValidators([Validators.required, Validators.pattern(this.ifeFormat.po_pattern.pattern), poNumberValidator(composePoName)]);
      } else {
        this.poFormGroup.controls['po_number'].setValidators([Validators.required, poNumberValidator(composePoName)])
      }
      // setValidators does not re-run them: without this the previous format's pattern error stays on
      // po_number, and its template (which reads this format's po_pattern) used to throw on every
      // change detection pass -- freezing the whole form (Chrome Runner e2e, PR #2761).
      this.poFormGroup.controls['po_number'].updateValueAndValidity();

      const currentDestination = this.poFormGroup.controls['po_destination'].value;
      if (!currentDestination || !this.poDestinations.find(d => d.destination_id === currentDestination)) {
        // null, never undefined: this value is saved on destroy and restored on the next visit, and
        // FormGroup.setValue treats an undefined key as missing (NG01002) -- an IFE format without
        // destinations then broke every later page load.
        this.poFormGroup.controls['po_destination'].setValue(this.poDestinations[0]?.destination_id ?? null);
      }
    })
    // todo retrieve email from user profile
    this.poFormGroup.controls['email_notification'].setValue(oAuthService.getIdentityClaims()['email']);
    this.poFormGroup.controls['email_notification_publish'].setValue(oAuthService.getIdentityClaims()['email']);

    this.poFormGroup.controls['po_destination'].valueChanges.subscribe(value => {
      if (value === 'west_entertainment') {
        if (!this.poFormGroup.controls['westent_cycle_name']) {
          this.poFormGroup.addControl('westent_cycle_name', formBuilder.control(null, Validators.required));
        }
      } else {
        this.poFormGroup.removeControl('westent_cycle_name');
      }
      if (value === 'lilt') {
        // Source language is locked to en/US for now (Lilt models are filtered to en/US
        // sources). When we want to support other source languages, lift these to user-editable.
        if (!this.poFormGroup.controls['lilt_src_lang']) {
          this.poFormGroup.addControl('lilt_src_lang', formBuilder.control('en', Validators.required));
        }
        if (!this.poFormGroup.controls['lilt_src_locale']) {
          this.poFormGroup.addControl('lilt_src_locale', formBuilder.control('US', Validators.required));
        }
        if (!this.poFormGroup.controls['lilt_domain_id']) {
          this.poFormGroup.addControl('lilt_domain_id', formBuilder.control(null, Validators.required));
        }
        if (!this.poFormGroup.controls['lilt_due_date']) {
          this.poFormGroup.addControl('lilt_due_date', formBuilder.control(null));
        }
        if (!this.poFormGroup.controls['lilt_source_review_required']) {
          this.poFormGroup.addControl('lilt_source_review_required', formBuilder.control(false));
        }
        if (!this.poFormGroup.controls['lilt_job_instructions']) {
          this.poFormGroup.addControl(
            'lilt_job_instructions',
            formBuilder.control(LILT_INSTRUCTIONS_REVIEW_NOT_REQUIRED, [
              Validators.required,
              Validators.maxLength(5000),
            ]),
          );
        }
        this.applyAutoSelectedLiltDomain();
      } else {
        this.poFormGroup.removeControl('lilt_src_lang');
        this.poFormGroup.removeControl('lilt_src_locale');
        this.poFormGroup.removeControl('lilt_domain_id');
        this.poFormGroup.removeControl('lilt_due_date');
        this.poFormGroup.removeControl('lilt_source_review_required');
        this.poFormGroup.removeControl('lilt_job_instructions');
      }
    })
    // Auto-select Lilt domain by airline marker when both inputs are known.
    this.poFormGroup.controls['airline'].valueChanges.subscribe(() => this.applyAutoSelectedLiltDomain());

    this.poService.getFormState().pipe(take(1)).subscribe(savedForm => {
      if (savedForm) {
        console.log(savedForm);
        // Migrated before both the push and the patch below, which would write the old shape back.
        savedForm = {...savedForm, poItems: (savedForm.poItems ?? []).map(movedDestinationKeys)};
        savedForm.poItems.forEach(poItem => (this.poFormGroup.controls.poItems as FormArray).push(this.itemGroup(poItem)));
        // patchValue, like the other PO forms: setValue throws on any control the saved state lacks
        // (or holds as undefined), e.g. state saved by an older build.
        this.poFormGroup.patchValue(savedForm)
      }
    });
    this.poService.poItemToAdd$.subscribe(data => {
      if (data) {
        (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(
          Object.assign(
            {},
            {
              basics: BasicPoitemInfoComponent.DEFAULTS,
              tracks: TrackSelectionComponent.DEFAULTS,
              above_specific: {},
              format_specific_data: {},
              destination_specific: {},
            },
            data)))
      }
    });
    this.subscriptions.push(this.poService.poItemToRemove$.subscribe(id => {
      if (id) {
        const index = (this.poFormGroup.controls.poItems as FormArray).controls.findIndex(poItem => poItem.get('id').value === id);
        (this.poFormGroup.controls.poItems as FormArray).removeAt(index);
      }
    }));
    effect(() => {
      if (this.airlines().length === 1) {
        this.poFormGroup.controls['airline'].setValue(this.airlines()[0].iata_code);
      }
    })
  }

  compare_item_id(o1: any, o2: any) {
    return o1?.item_id === o2?.item_id;
  }

  compare_destination_id(o1: any, o2: any) {
    return o1?.destination_id === o2?.destination_id;
  }

  poItemTrackBy: TrackByFunction<FormGroup> = (index: number, poitem: FormGroup) => {
    return poitem.get('id').value;
  }

  load(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result as string);
        console.log(json)
        this.poFormGroup.patchValue(json);
        console.log(this.poFormGroup.value)
        this.poFormGroup.controls.poItems.setValue([]);
        json['poItems'].forEach(poItem => (this.poFormGroup.controls.poItems as FormArray).push(this.itemGroup(poItem)));
      } catch (error) {
        console.error('Error parsing JSON:', error);
        alert('Invalid JSON file');
      }
    };

    reader.onerror = () => {
      console.error('Error reading file:', reader.error);
      alert('Error reading file');
    };

    reader.readAsText(file);
  }

  /** A saved or loaded item, completed with controls added since it was saved. */
  private itemGroup(poItem: any): FormGroup {
    return this.formBuilder.group({destination_specific: {}, ...movedDestinationKeys(poItem)});
  }

  /** The last template_capabilities request failed (as opposed to: still loading). */
  capabilitiesFailed = false;

  private loadCapabilities(vtkTemplate?: string): void {
    this.capabilitiesSub?.unsubscribe();
    this.capabilitiesFailed = false;
    this.burnInSupported = null;
    this.capabilities = null;
    this.fsdSchema = null;
    this.watermarkSelection = null;
    this.templateDescription = null;
    if (!vtkTemplate) {
      return;
    }
    this.capabilitiesSub = this.apollo.use('workflow').query<{ template_capabilities: TemplateCapabilities }>({
      query: TEMPLATE_CAPABILITIES,
      variables: {vtk_template: vtkTemplate},
    }).subscribe({
      next: r => {
        this.capabilities = r.data?.template_capabilities ?? null;
        this.burnInSupported = this.capabilities ? !!this.capabilities.supports_burnt_in_texttracks : null;
        this.fsdSchema = parseSchema(this.capabilities?.format_specific_data_schema);
        this.watermarkSelection = parseSchema(this.capabilities?.forensic_watermark_selection) as WatermarkSelection | null;
        this.templateDescription = this.capabilities?.description || null;
      },
      error: err => {
        // Not knowing is not "no": nothing burnt-in is offered, no settings are shown, submit
        // refuses until capabilities are known, and the reason is logged.
        console.error(`template_capabilities(${vtkTemplate}) failed`, err);
        this.capabilitiesFailed = true;
        this.burnInSupported = null;
        this.capabilities = null;
        this.fsdSchema = null;
      },
    });
  }

  /**
   * The item's source video duration in seconds, for auto-selecting the watermark: null while
   * unknown, when the source has no video track, or when the template takes no video. The first
   * call for a source starts loading its probe.
   */
  sourceDurationFor(brefix: string | null | undefined): number | null {
    if (!brefix || !this.capabilities || !this.sendsVideo(this.capabilities)) {
      return null;
    }
    return this.sourceDurations.of(brefix);
  }

  /**
   * Whether the item is sent with its video track: the template must take one, and the format must
   * not be an audio-only one (e.g. "Copy source files as is (No video)" on mezzanine_default, whose
   * video track is optional).
   */
  private sendsVideo(capabilities: TemplateCapabilities): boolean {
    return !this.ifeFormat?.audio_only && this.takes(capabilities.video_track);
  }

  /** Whether the template takes this kind of track at all (optional or required). */
  private takes(requirement: TrackRequirement | null | undefined): boolean {
    return requirement === TrackRequirement.Optional || requirement === TrackRequirement.Required;
  }

  /**
   * Writes the item's title card next to its sources and completes once the copy has finished; for an
   * item without one it completes at once. The card file is the item's burnt-in subtitle (VTK-3398).
   */
  private writeTitlecard(source: any): Observable<unknown> {
    if (!source.tracks?.titlecard) {
      return of(null);
    }
    return this.apollo.mutate<RepositoryMutation>({
      mutation: COPY_FILE,
      variables: {src: titlecardDataUri(source.tracks.titlecard_text), dest: titlecardDestination(source.basics.input_brefix)},
    }).pipe(
      switchMap(r => this.processService.observeProcess(r.data.copy_file, {
        failureMessage: `Failed to write the title card for ${source.basics.po_item_id}`,
      })),
      last(),
      map(p => {
        if (p.state !== 'SUCCESS') {
          throw new Error(`title card for ${source.basics.po_item_id}: ${p.message || p.state}`);
        }
        return p;
      }),
    );
  }

  /** Set once a submit was refused: from then on every invalid field and PO item shows as such. */
  submitted = false;

  /** Whether a PO-level control shows its error: invalid, and touched or after a refused submit. */
  showInvalid(name: string): boolean {
    const c = this.poFormGroup.controls[name];
    return !!c && c.invalid && (c.touched || this.submitted);
  }

  submit_po(button: DoButtonComponent) {
    if (!this.poFormGroup.valid) {
      this.submitted = true;
      console.log(this.poFormGroup)
      reportInvalid(button, this.poFormGroup, 'ife-po-form');
      this.poFormGroup.markAllAsTouched();
      return
    }
    if (this.poFormGroup.value.po_destination === 'lilt') {
      for (const source of this.poFormGroup.value.poItems) {
        const totalSubCc = (source.tracks.subtitles?.length || 0) + (source.tracks.cc?.length || 0);
        if (totalSubCc > 1) {
          button.done('Lilt destination supports at most one subtitle or closed caption track per POItem.', {severity: 'cl-alert-danger'});
          return;
        }
        if (totalSubCc < 1 && !LILT_AUDIO_ONLY_SUPPORTED) {
          button.done('Lilt destination requires exactly one subtitle or closed caption track per POItem.', {severity: 'cl-alert-danger'});
          return;
        }
      }
    }
    if (!this.capabilities) {
      button.done('The template\'s capabilities are not loaded yet; try again in a moment.', {severity: 'cl-alert-danger'});
      return;
    }
    const capabilities = this.capabilities;
    const workflows = []
    const emails = this.poFormGroup.value.email_notification.split(',').map(e => e.trim()).filter(e => e !== '')
    const emailsPublish = this.poFormGroup.value.email_notification_publish?.split(',').map(e => e.trim()).filter(e => e !== '') || []
    const organization = this.organization().id
    for (const source of this.poFormGroup.value.poItems) {
      let metadataSource: string | null = null;
      // The schema form's output (already pruned to the keys the template declares), then the
      // destination's keys (thales_fastpath_airline, lilt_*), which the template does not declare
      // and the schema form would strip. A format carries no fixed values of its own: a template
      // with defaults has an empty schema, so this is {} and the defaults apply.
      const formatSpecificData = {
        ...(source.format_specific_data || {}),
        ...(source.destination_specific || {}),
      };
      if (this.poFormGroup.value.po_destination === 'lilt') {
        const urls = (formatSpecificData.lilt_reference_urls || '')
          .split('\n').map((u: string) => u.trim()).filter((u: string) => u !== '');
        metadataSource = urls.length ? `lilt-refs://${urls.join(',')}` : null;
        delete formatSpecificData.lilt_reference_urls;
        // Stamp PO-level Lilt fields onto each item so the backend manifest writer
        // (which reads them from format_specific_data) doesn't need to know about
        // the PO-vs-item split.
        formatSpecificData.lilt_domain_id = this.poFormGroup.value.lilt_domain_id;
        formatSpecificData.lilt_src_lang = this.poFormGroup.value.lilt_src_lang;
        formatSpecificData.lilt_src_locale = this.poFormGroup.value.lilt_src_locale;
        // Due date is optional — only serialise when the operator picked one.
        formatSpecificData.lilt_due_date = liltDueInstant(this.poFormGroup.value.lilt_due_date);
        formatSpecificData.lilt_source_review_required = !!this.poFormGroup.value.lilt_source_review_required;
        formatSpecificData.lilt_job_instructions = this.poFormGroup.value.lilt_job_instructions;
      } else if (this.ifeFormat.metadata_source_type) {
        metadataSource = this.ifeFormat.metadata_source_type + '://' + source.above_specific.metadata_reference;
      }
      if (source.tracks?.titlecard) {
        // Drawn where its cue puts it (bottom-left), not at the template's default subtitle position.
        formatSpecificData.burntin_subtitle_position = TITLECARD_BURNIN_POSITION;
      }
      workflows.push(this.writeTitlecard(source).pipe(switchMap(() => this.apollo.use('workflow').mutate<Mutation>({
        mutation: START_WORKFLOW_DEFAULT,
        variables: {
          email_notification: emails,
          email_notification_publish: emailsPublish.length > 0 ? emailsPublish : null,
          auto_publish: this.poFormGroup.value.auto_publish,
          aspect_ratio: source.above_specific.aspect_ratio,
          filename: (source.basics.filename || "").trim(),
          metadata_source: metadataSource,
          wrap_up_workflow: this.ifeFormat.wrap_up_workflow,
          content_identifier: `s3://${source.basics.input_brefix}`,
          po_item_id: source.basics.po_item_id.trim(),
          po_name: composePoName(this.poFormGroup),
          po_destination: this.poFormGroup.value.po_destination,
          vtk_template: this.ifeFormat.vtk_template,
          format_specific_data: JSON.stringify(formatSpecificData),
          watermark: source.above_specific.watermark,
          tracks:
            (this.sendsVideo(capabilities) ? [{'codec_type': CodecType.Video}] : [])
              .concat(
                source.tracks.audio.map(t => {
                  return {
                    'codec_type': CodecType.Audio,
                    'lang': t
                  }
                })
              ).concat(
              source.tracks.subtitles.map(t => {
                return {
                  'codec_type': CodecType.Subtitle,
                  'lang': t
                }
              })
            ).concat(
              source.tracks.cc.map(t => {
                return {
                  'codec_type': CodecType.Closedcaption,
                  'lang': t
                }
              })
            ).concat(
              // The title card replaces the burnt-in subtitle selection (the selector is locked while it is on).
              source.tracks.titlecard ? [{'codec_type': CodecType.BurntinSubtitle, 'lang': TITLECARD_LANG}] :
              // Parse burntin value format "lang:type" e.g. "en:subtitle" or "en:closedcaption"
              source.tracks.burntin ? (() => {
                const [lang, type] = source.tracks.burntin.split(':');
                return [{
                  'codec_type': type === 'subtitle' ? CodecType.BurntinSubtitle : CodecType.BurntinClosedcaption,
                  'lang': lang
                }];
              })() : []
            ).concat(
              (this.takes(capabilities.image_track) ? source.tracks.images.map(t => {
                return {
                  'codec_type': CodecType.Image,
                  'filename': t,
                }
              }) : []))
        } satisfies StartWorkflowDefaultInput
      }).pipe(
        map(d => ({
          process: d.data.start_workflow_default,
          po_item_id: source.basics.po_item_id.trim()
        }))))))
    }


    forkJoin(workflows).subscribe(
      {
        next: wfs => {
          button.done('All encodes started successfully.');
          wfs.forEach(workflow => {
            this.processService.observeProcess(workflow.process, {
              successMessage: `${workflow.po_item_id} encoded.`,
              failureMessage: `Failed to encode ${workflow.po_item_id}.`,
              link: ['/#/o', organization, 'workflows',
                this.poFormGroup.value.airline,
                `${this.poFormGroup.value.airline}_${this.poFormGroup.value.po_number}`].join('/')
            })
          })
        },
        error: err => {
          // Kept on screen (negative duration): an operator who looks away for 5 s must still see why
          // nothing started (e.g. "Not Authorized to access copy_file" while writing a title card).
          button.done('Workflow submission failed: ' + (err?.message || err), {severity: 'cl-alert-danger', duration: -1});
        }
      }
    )
  }

  readonly startEncodeLabel = startEncodeLabel;

  get poItems() {
    return this.poFormGroup.get('poItems') as FormArray<FormGroup>;
  }

  get fields() {
    return this.poFormGroup.controls;
  }

  a(a: any) {
    console.log(a)
  }

  ngOnDestroy(): void {
    this.poService.setFormState(this.poFormGroup.value)
    this.subscriptions.forEach(s => s.unsubscribe())
    this.capabilitiesSub?.unsubscribe()
  }

  get selectedLiltDomain(): LiltDomain | null {
    const id = this.poFormGroup.controls['lilt_domain_id']?.value;
    if (!id) return null;
    return this.liltDomains.find(d => d.domain_id === id) || null;
  }

  onLiltDomainChange(): void {
    // The format-specific component drops any pair selections that no longer match
    // the new domain via its own recompute — nothing to do here at PO level.
  }

  onLiltSourceReviewToggle(checked: boolean): void {
    // Seed the job-instructions textarea to match the checkbox state. Overwrites the
    // current value on every toggle — operators who tinker heavily should make their
    // final edits after settling on the checkbox state.
    this.poFormGroup.controls['lilt_source_review_required']?.setValue(checked);
    this.poFormGroup.controls['lilt_job_instructions']?.setValue(
      checked ? LILT_INSTRUCTIONS_REVIEW_REQUIRED : LILT_INSTRUCTIONS_REVIEW_NOT_REQUIRED,
    );
  }

  private applyAutoSelectedLiltDomain(): void {
    const domainCtrl = this.poFormGroup.controls['lilt_domain_id'];
    if (!domainCtrl) return;
    // Don't stomp on an existing manual choice.
    if (domainCtrl.value) return;
    const airline = this.poFormGroup.controls['airline'].value;
    if (!airline) return;
    // Match domains whose name contains "airline: <CODE>" (case-insensitive).
    const needle = `airline: ${airline}`.toLowerCase();
    const match = this.liltDomains.find(d => (d.name || '').toLowerCase().includes(needle));
    if (match) {
      domainCtrl.setValue(match.domain_id);
    }
  }

  downloadPO() {
    const json = JSON.stringify(this.poFormGroup.value, null, 2);
    const blob = new Blob([json], {type: 'application/json'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const now = new Date();
    const datetime = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}-${now.getMinutes().toString().padStart(2, '0')}-${now.getSeconds().toString().padStart(2, '0')}`;
    const filename = `${this.poFormGroup.value.airline}_${this.poFormGroup.value.po_number.trim()}-${datetime}.json`;
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    console.log('savedForm2')
  }

  handlePasteFilenames(clipboardText: string, startIndex: number) {
    const lines = clipboardText.split(/\r?\n/).map(l => l.trim()).filter(l => l);
    if (!lines.length) {
      return;
    }

    for (let i = 0; i < lines.length; i++) {
      const item = this.poItems.at(startIndex + i);
      if (!item) {
        break;
      }

      const basics = item.get('basics');
      basics?.get('filename')?.setValue(lines[i]);
    }
  }
}


